import { Trash2, Search, Plus, Download, Eye } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { ChecklistDetail } from '../types';

interface ChecklistHeaderProps {
  checklist: ChecklistDetail;
  filter: string;
  onFilterChange: (value: string) => void;
  onDelete: () => void;
  onAddItem: () => void;
  onImport: () => void;
  onWatchAll: () => void;
  allOnWatchlist: boolean;
  isWatchAllPending: boolean;
  isDeleting: boolean;
}

export function ChecklistHeader({
  checklist,
  filter,
  onFilterChange,
  onDelete,
  onAddItem,
  onImport,
  onWatchAll,
  allOnWatchlist,
  isWatchAllPending,
  isDeleting,
}: ChecklistHeaderProps) {
  const { t } = useLanguage();

  return (
    <div className="p-4 border-b border-gray-100">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 truncate">
            {checklist.name}
          </h2>
          {checklist.description && (
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
              {checklist.description}
            </p>
          )}
        </div>
        <button
          onClick={onDelete}
          disabled={isDeleting}
          className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
          title={t('checklists.deleteChecklist', 'Delete checklist')}
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      {/* Actions row */}
      <div className="flex flex-wrap items-center gap-2 mt-4">
        {/* Search input */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('checklists.searchItems', 'Search items...')}
            value={filter}
            onChange={e => onFilterChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow"
          />
        </div>

        {/* Add button */}
        <button
          onClick={onAddItem}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors shadow-sm hover:shadow"
        >
          <Plus className="w-4 h-4" />
          {t('checklists.addItem', 'Add')}
        </button>

        {/* Import button */}
        <button
          onClick={onImport}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
        >
          <Download className="w-4 h-4" />
          {t('checklists.import', 'Import')}
        </button>

        {/* Watch all button */}
        <button
          onClick={onWatchAll}
          disabled={isWatchAllPending || allOnWatchlist}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={t('checklists.addAllToWatchlist', 'Add all items to watchlist')}
        >
          <Eye className="w-4 h-4" />
          {t('checklists.watchAll', 'Watch All')}
        </button>
      </div>
    </div>
  );
}
